import { useEffect, useState } from 'react';

import Loader from '@/components/atoms/Loader';
import { EMPTY_WELLNESS_DOMAINS } from '@/constants';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
// import UserWellnessGoal from '@/modules/user/UserWellnessGoal'; // removed for now; could be added back later
import { useAppStore } from '@/store/useAppStore';
import { getAssessmentsResultLastScore } from '@/utils/queryOptions';
import { Box, Center, HStack, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import UserNotes from '@/modules/user/UserNotes';
import roles from '@/constants/roles';
import UserPeerSupportBioModal from '@/modules/user/UserPeerSupportBioModal';
import UserSupportTeamSection from '@/modules/user/UserSupportTeamSection';
import { useUserDashboard } from '@/hooks/user/useUserDashboard';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import WholenessRingWithOverview from '@/components/organisms/WholenessRingWithOverview';
import WholenessTrends from '@/components/molecules/WholenessTrends';
import svgIcons from '@/assets/svg/icons';
import { employeeUserRoutes } from '@/constants/routes/app';

const UserDashboard = () => {
  const { user: userData } = useAppStore();
  const navigate = useNavigate({ from: employeeUserRoutes.userDashboard });

  const [isNewPeerSupportModalOpen, setIsNewPeerSupportModalOpen] = useState(false);

  useEffect(() => {
    if (userData?.roles.includes(roles.peerSupport) && !userData.user_metadata.bio) {
      setIsNewPeerSupportModalOpen(true);
    }
  }, [userData]);

  const {
    data: userDashboard,
    isLoading: isLoadingUserDashboard,
    isRefetching: isRefetchingUserDashboard,
  } = useUserDashboard();

  const isLoading = !userData || !userDashboard || userDashboard?.score.domains === EMPTY_WELLNESS_DOMAINS;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <VStack width={MAX_CONTAINER_WIDTH}>
      {/* Actual page content */}
      <VStack width={'100%'} marginBottom={'50px'} gap={'80px'}>
        {/* Wholeness Ring and Overview */}
        <VStack marginTop={'20px'} width={'100%'}>
          {isLoadingUserDashboard || isRefetchingUserDashboard ? (
            <Center height={'530px'} marginBottom={'70px'}>
              <LoadingLogo />
            </Center>
          ) : (
            <VStack width={'100%'} gap={'100px'}>
              <WholenessRingWithOverview
                dashboardInsights={userDashboard ? userDashboard.insights : undefined}
                dashboardScores={userDashboard ? userDashboard.score : undefined}
                wholenessRingAvatar={userData?.user_metadata.profilePicture}
                overviewBottomButtonIconPath={svgIcons.assessment}
                overviewBottomButtonLabel="Retake Screener"
                overviewBottomButtonOnClick={() =>
                  navigate({ to: '/screener/$screenerId', params: { screenerId: 'wholeness-screener' } })
                }
              />
              <WholenessTrends dashboardTrendData={userDashboard.insights ? userDashboard.trends : null} />
            </VStack>
          )}
        </VStack>

        {/* Call emergency */}
        <Box
          backgroundColor={'background.cloudGray'}
          marginY={'40px'}
          paddingY={'40px'}
          paddingX={'80px'}
          borderRadius={'12px'}
          fontSize={'16px'}
          lineHeight={'20px'}
          fontWeight={500}
          maxWidth={'600px'}
        >
          <Text display={'inline'} variant={'lora'} fontStyle={'italic'}>
            If you feel like you are having a medical emergency{' '}
          </Text>
          <Text display={'inline'} fontWeight={800} color={'primary.500'}>
            dial 911
          </Text>
          <VStack display={'inline-block'} marginTop={'20px'} textAlign={'center'}>
            <Text display={'inline'} variant={'lora'} fontSize={'16px'} lineHeight={'20px'} fontStyle={'italic'}>
              If you are considering suicide, or struggling with your mental health and are having a crisis{' '}
            </Text>
            <Text display={'inline'} fontWeight={800} color={'primary.500'}>
              dial 988
            </Text>
          </VStack>
        </Box>

        {/* User Notes */}
        <HStack width={'100%'} justifyContent={'center'} gap={'40px'} alignItems={'center'}>
          <UserNotes memberId={userData.user_id} />
        </HStack>

        {/* Care Specialists (ex Bobbi section) */}
        <VStack marginTop={'100px'}>
          {/* , Therapist */}
          <Text variant={'loraMediumTitle'} color={'text.darkBlue'}>
            If you need help you can reach out to Member Care or Peer Support.
          </Text>
          <Text variant={'urbanistMediumLarge'} fontWeight={400} color={'text.mediumGray'} marginBottom={'50px'}>
            Choose what type of help might be best for you.
          </Text>

          <UserSupportTeamSection data={userDashboard.supportTeam} isLoading={isLoadingUserDashboard} />
        </VStack>
      </VStack>

      <UserPeerSupportBioModal
        isModalOpen={isNewPeerSupportModalOpen}
        userMetadata={userData.user_metadata}
        setIsModalOpen={setIsNewPeerSupportModalOpen}
      />

      {/* TODO: removing for now */}
      {/* <OrganizationFeed /> */}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_user/user/dashboard')({
  component: UserDashboard,
  loader: async ({ context }) => context.queryClient.ensureQueryData(getAssessmentsResultLastScore()),
});
